import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, Loader2, CheckCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = sp.get('token') || '';
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('Your password needs to be at least 8 characters long.');
    if (!token) return setError('This reset link is missing information. Please request a new one.');
    setLoading(true);
    try {
      await resetPassword({ token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Could not reset your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto py-4 fade-in">
        <div className="bg-white rounded-3xl border-2 border-success/30 shadow-sm p-8 md:p-10 text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-text mb-3">Password reset</h1>
          <p className="text-text-muted text-lg mb-6">You can now sign in with your new password.</p>
          <Link to="/login"><Button className="rounded-xl py-5 px-8 text-lg font-bold">Go to sign in</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-4 fade-in">
      <div className="bg-white rounded-3xl border-2 border-stone-200 shadow-sm p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <KeyRound className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">Choose a new password</h1>
        </div>
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-base font-semibold text-text mb-2">New password</label>
            <div className="relative">
              <Lock className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="reset-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-14 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="At least 8 characters"
              />
              <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-text-muted hover:text-text">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {error && <div data-testid="reset-error" className="bg-error/10 text-error border border-error/30 rounded-xl p-4 text-base">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-6 text-lg font-bold">
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
            Save new password
          </Button>
        </form>
      </div>
    </div>
  );
}
